import { useEffect, useId, useMemo, useState, type CSSProperties } from "react";
import {
  adminGetAppSettings,
  adminListIngredients,
  adminCreateIngredient,
  adminUpdateIngredient,
  type AdminIngredientFull,
  type AdminIngredientMaster,
} from "../../lib/api";
import IngredientImpactModal from "../../components/admin/IngredientImpactModal";
import { useDialogA11y } from "../../lib/useDialogA11y";
import RecipeStudioTabs from "../../components/admin/RecipeStudioTabs";
import { supabase } from "../../lib/supabase";
import { readAllRows } from "../../lib/readAllRows";

/**
 * Admin Ingredients — list, search, create, edit, toggle availability.
 * Toggling availability on a unavailable→true is one-click; available→false
 * surfaces the IngredientImpactModal that lets the manager 86 affected drinks.
 *
 * Auth: bar_manager+ (matches the underlying edge functions). bar_owner sees
 * the same UI; no owner-only actions in this section.
 */

// Must match the live bar_manual.ingredients_category_check — anything else fails the insert.
const CATEGORIES = [
  "spirit",
  "liqueur",
  "bitters",
  "syrup",
  "juice",
  "garnish",
  "prep_item",
  "dairy",
  "egg",
  "wine",
  "vermouth",
  "other",
];

const UNITS = ["oz", "ml", "dash", "drops", "barspoon", "splash", "each", "leaf", "sprig", "wedge"];

export default function AdminIngredients() {
  const [items, setItems] = useState<AdminIngredientMaster[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState<"all" | "available" | "unavailable" | "untracked">("all");
  const [editing, setEditing] = useState<AdminIngredientFull | null>(null);
  const [creating, setCreating] = useState(false);
  const [impactTarget, setImpactTarget] = useState<{ ingredient: AdminIngredientFull; goingTo: boolean } | null>(null);
  const [flash, setFlash] = useState<string | null>(null);
  // Phase 10 polish: cascade-86 default in the impact modal follows the
  // app_settings.auto_86_on_unavailable_ingredient flag (false by default).
  const [autoCascadeDefault, setAutoCascadeDefault] = useState(false);

  // Pull from Inventory Modal state
  const [showPullInventoryModal, setShowPullInventoryModal] = useState(false);
  const [invItems, setInvItems] = useState<Array<{ id: string; name: string; category_slug?: string; subcategory?: string }>>([]);
  const [invLoading, setInvLoading] = useState(false);
  const [invSearch, setInvSearch] = useState("");
  const [pullingId, setPullingId] = useState<string | null>(null);

  async function loadInventoryItems() {
    setInvLoading(true);
    try {
      const data = await readAllRows<{ id: string; name: string; subcategory?: string; category_slug?: string }>((from, to) =>
        supabase
          .from("menu_items")
          .select("id, name, subcategory, category_slug")
          .order("name", { ascending: true })
          .order("id", { ascending: true })
          .range(from, to)
      );
      setInvItems(data);
    } catch (e) {
      console.warn("Failed to fetch menu_items:", e);
    } finally {
      setInvLoading(false);
    }
  }

  useEffect(() => {
    if (showPullInventoryModal && invItems.length === 0) {
      loadInventoryItems();
    }
  }, [showPullInventoryModal]);

  async function handlePullItem(item: { id: string; name: string; subcategory?: string; category_slug?: string }) {
    setPullingId(item.id);
    try {
      let inferredCat = "spirit";
      const sub = (item.subcategory || item.category_slug || "").toLowerCase();
      const n = item.name.toLowerCase();
      // "Wine Cask Finish" whiskies and "Wine Slushie Mix" name a wine without being one.
      const nameIsWine = (n.includes("prosecco") || n.includes("wine")) && !/cask|barrel|finish|oak|mix/.test(n);
      if (sub.includes("vermouth") || n.includes("vermouth")) inferredCat = "vermouth";
      else if (sub.includes("wine") || sub.includes("prosecco") || sub.includes("champagne") || sub.includes("sherry") || nameIsWine) inferredCat = "wine";
      else if (sub.includes("liqueur") || n.includes("liqueur") || n.includes("amaro")) inferredCat = "liqueur";
      else if (sub.includes("bitter") || n.includes("bitters")) inferredCat = "bitters";
      else if (sub.includes("syrup") || n.includes("syrup")) inferredCat = "syrup";
      else if (sub.includes("beer") || n.includes("beer") || n.includes("cider")) inferredCat = "other";
      else if (n.includes("water") || n.includes("soda") || n.includes("tonic")) inferredCat = "other";

      const res = await adminCreateIngredient({
        name: item.name,
        category: inferredCat,
        default_unit: "oz",
      });
      if (res.ok && res.ingredient) {
        showFlash(`Pulled "${item.name}" into recipe ingredients!`);
        reload();
      }
    } catch (err: any) {
      showFlash(err?.message || "Failed to pull ingredient");
    } finally {
      setPullingId(null);
    }
  }

  async function handleQuickAddEssential(name: string, category: string, default_unit = "oz", is_untracked = true) {
    try {
      const res = await adminCreateIngredient({
        name,
        category,
        default_unit,
        is_untracked,
      });
      if (res.ok && res.ingredient) {
        showFlash(`Added "${name}" to recipe ingredients!`);
        reload();
      }
    } catch (err: any) {
      showFlash(err?.message || "Failed to create ingredient");
    }
  }

  async function reload() {
    setLoading(true);
    setError(null);
    try {
      const r = await adminListIngredients();
      setItems(r.ingredients);
    } catch (err: any) {
      setError(err?.message ?? "Could not load ingredients");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    reload();
    void adminGetAppSettings()
      .then((r) => {
        if (r.settings) setAutoCascadeDefault(!!r.settings.auto_86_on_unavailable_ingredient);
      })
      .catch(() => undefined);
  }, []);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return items.filter((i) => {
      if (filter === "untracked" && !i.is_untracked) return false;
      if (filter === "available" && (!i.available || i.is_untracked)) return false;
      if (filter === "unavailable" && i.available) return false;
      if (!needle) return true;
      return (
        i.name.toLowerCase().includes(needle) ||
        i.category.toLowerCase().includes(needle)
      );
    });
  }, [items, q, filter]);

  function showFlash(msg: string) {
    setFlash(msg);
    setTimeout(() => setFlash(null), 3000);
  }

  function startToggleAvailability(ing: AdminIngredientMaster) {
    setImpactTarget({
      ingredient: ing as AdminIngredientFull,
      goingTo: !ing.available,
    });
  }

  return (
    <section style={styles.shell}>
      <RecipeStudioTabs activeTab="ingredients" badgeCounts={{ ingredients: items.length }} />
      <div style={styles.headerRow}>
        <div>
          <p style={styles.eyebrow}>Service controls</p>
          <h1 style={styles.title}>Ingredients</h1>
        </div>
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>

          <button type="button" onClick={() => setCreating(true)} style={styles.primaryBtn}>
            + New Ingredient
          </button>
        </div>
      </div>

      <div style={styles.toolbar}>
        <input
          type="text"
          placeholder="Search ingredients…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          style={styles.search}
        />
        <div style={styles.filterRow}>
          {(["all", "available", "unavailable", "untracked"] as const).map((f) => (
            <button
              key={f}
              type="button"
              onClick={() => setFilter(f)}
              style={filter === f ? styles.pillActive : styles.pill}
            >
              {f === "all"
                ? "All"
                : f === "available"
                  ? "Available"
                  : f === "unavailable"
                    ? "Unavailable"
                    : "Untracked"}
            </button>
          ))}
        </div>
      </div>

      {flash && <div style={styles.flash}>{flash}</div>}
      {error && <div style={styles.error}>{error}</div>}
      {loading && <p style={styles.muted}>Loading ingredients…</p>}

      {!loading && (
        <div style={styles.tableWrap}>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Name</th>
                <th style={styles.th}>Category</th>
                <th style={styles.th}>Default Unit</th>
                <th style={styles.thCenter}>Allergens</th>
                <th style={styles.thCenter}>Status</th>
                <th style={styles.thRight}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((i) => (
                <tr key={i.id} style={styles.tr}>
                  <td style={styles.td}>
                    <strong style={styles.name}>{i.name}</strong>
                    {i.is_untracked && <span style={styles.untrackedBadge}>Untracked</span>}
                    {i.notes && <div style={styles.subnote}>{i.notes}</div>}
                  </td>
                  <td style={styles.td}>{i.category}</td>
                  <td style={styles.td}>{i.default_unit}</td>
                  <td style={styles.tdCenter}>
                    {i.allergen_flags.length === 0
                      ? <span style={styles.subnote}>—</span>
                      : (
                        <div style={styles.allergenRow}>
                          {i.allergen_flags.map((a) => (
                            <span key={a} style={styles.allergenChip}>{a}</span>
                          ))}
                        </div>
                      )}
                  </td>
                  <td style={styles.tdCenter}>
                    {i.is_untracked ? (
                      <span style={styles.statusUntracked}>Always Available</span>
                    ) : (
                      <span style={i.available ? styles.statusAvail : styles.statusUnavail}>
                        {i.available ? "Available" : "Unavailable"}
                      </span>
                    )}
                  </td>
                  <td style={styles.tdRight}>
                    {i.is_untracked ? (
                      <span style={styles.alwaysAvailLabel}>Exempt from 86</span>
                    ) : (
                      <button
                        type="button"
                        onClick={() => startToggleAvailability(i)}
                        style={i.available ? styles.btnRed : styles.btnGreen}
                      >
                        {i.available ? "Mark Unavailable" : "Mark Available"}
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => setEditing(i as AdminIngredientFull)}
                      style={styles.btnSecondary}
                    >
                      Edit
                    </button>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={6} style={styles.empty}>
                    {items.length === 0
                      ? "No ingredients yet. Click \"+ New Ingredient\" to add one."
                      : "No matching ingredients."}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {(creating || editing) && (
        <IngredientForm
          initial={editing}
          onClose={() => {
            setCreating(false);
            setEditing(null);
          }}
          onSaved={(label) => {
            setCreating(false);
            setEditing(null);
            showFlash(label);
            reload();
          }}
        />
      )}

      {impactTarget && (
        <IngredientImpactModal
          ingredient={impactTarget.ingredient}
          goingTo={impactTarget.goingTo}
          autoCascadeDefault={autoCascadeDefault}
          onClose={() => setImpactTarget(null)}
          onConfirmed={(updatedAvailable, cascadeCount) => {
            setImpactTarget(null);
            showFlash(
              updatedAvailable
                ? "Marked available."
                : cascadeCount > 0
                  ? `Marked unavailable. ${cascadeCount} ${cascadeCount === 1 ? "drink was" : "drinks were"} 86'd.`
                  : "Marked unavailable.",
            );
            reload();
          }}
        />
      )}
      {/* Pull From Inventory Modal */}
      {showPullInventoryModal && (
        <div style={styles.modalBackdrop} onClick={() => setShowPullInventoryModal(false)}>
          <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <div>
                <h2 style={styles.modalTitle}>📥 Pull from Inventory & Catalog</h2>
                <p style={styles.modalMeta}>
                  Search bottles in the inventory catalog and import them into the recipe database in 1 tap
                </p>
              </div>
              <button
                type="button"
                style={styles.closeBtn}
                onClick={() => setShowPullInventoryModal(false)}
              >
                ✕
              </button>
            </div>

            {/* Quick Essentials Chips */}
            <div style={{ marginBottom: 16 }}>
              <span style={{ fontSize: 11, fontWeight: 800, textTransform: "uppercase", color: "var(--muted)", letterSpacing: 0.5, display: "block", marginBottom: 6 }}>
                Quick Add Bar Essentials:
              </span>
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                {[
                  { name: "Water", cat: "other" },
                  { name: "Mionetto Prosecco", cat: "wine" },
                  { name: "Club Soda", cat: "other" },
                  { name: "Tonic Water", cat: "other" },
                  { name: "Sparkling Water", cat: "other" },
                  { name: "Saline Solution (20%)", cat: "bitters" },
                ].map((ess) => {
                  const alreadyExists = items.some((i) => i.name.toLowerCase() === ess.name.toLowerCase());
                  return (
                    <button
                      key={ess.name}
                      type="button"
                      disabled={alreadyExists}
                      onClick={() => handleQuickAddEssential(ess.name, ess.cat)}
                      style={{
                        ...styles.essentialChip,
                        ...(alreadyExists ? styles.essentialChipExists : {}),
                      }}
                    >
                      {alreadyExists ? `✓ ${ess.name}` : `+ ${ess.name}`}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Search Input */}
            <div style={{ marginBottom: 14 }}>
              <input
                type="text"
                value={invSearch}
                onChange={(e) => setInvSearch(e.target.value)}
                placeholder="Search bottles (e.g. Prosecco, Fords Gin, Campari, Water)..."
                style={styles.input}
                autoFocus
              />
            </div>

            {/* Instant Create if not in inventory */}
            {invSearch.trim().length > 1 && (
              <div style={styles.customAddBox}>
                <div>
                  <span style={{ fontSize: 13, color: "var(--text)", fontWeight: 700 }}>
                    Add "{invSearch.trim()}" directly as new recipe ingredient
                  </span>
                  <div style={{ fontSize: 11, color: "var(--muted)" }}>
                    Creates a custom ingredient immediately
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    handleQuickAddEssential(invSearch.trim(), "spirit");
                    setInvSearch("");
                  }}
                  style={styles.primaryBtn}
                >
                  + Add to Recipes
                </button>
              </div>
            )}

            {/* Inventory Items List */}
            <div style={{ maxHeight: 340, overflowY: "auto", border: "1px solid var(--border)", borderRadius: 8, padding: 4 }}>
              {invLoading && <p style={styles.muted}>Loading inventory catalog…</p>}
              {!invLoading && invItems
                .filter((b) => !invSearch || b.name.toLowerCase().includes(invSearch.toLowerCase()))
                .slice(0, 50)
                .map((b) => {
                  const alreadyInRecipes = items.some((i) => i.name.toLowerCase() === b.name.toLowerCase());
                  const isPulling = pullingId === b.id;
                  return (
                    <div
                      key={b.id}
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        padding: "8px 12px",
                        borderBottom: "1px solid rgba(255,255,255,0.03)",
                        gap: 10,
                      }}
                    >
                      <div>
                        <div style={{ fontSize: 14, fontWeight: 700, color: "var(--text)" }}>{b.name}</div>
                        <div style={{ fontSize: 11, color: "var(--muted)" }}>
                          {b.subcategory || b.category_slug || "Spirit"}
                        </div>
                      </div>
                      <div>
                        {alreadyInRecipes ? (
                          <span style={{ fontSize: 11, color: "var(--green, #27ae60)", fontWeight: 700 }}>
                            ✓ In Recipes
                          </span>
                        ) : (
                          <button
                            type="button"
                            disabled={isPulling}
                            onClick={() => handlePullItem(b)}
                            style={styles.pullBtn}
                          >
                            {isPulling ? "Pulling…" : "+ Pull into Recipes"}
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
            </div>

            <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 16 }}>
              <button
                type="button"
                style={styles.cancelBtn}
                onClick={() => setShowPullInventoryModal(false)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

interface IngredientFormProps {
  initial: AdminIngredientFull | null;
  onClose: () => void;
  onSaved: (flashMsg: string) => void;
}

function IngredientForm({ initial, onClose, onSaved }: IngredientFormProps) {
  const isEdit = !!initial;
  const [name, setName] = useState(initial?.name ?? "");
  const [category, setCategory] = useState(initial?.category ?? "spirit");
  const [defaultUnit, setDefaultUnit] = useState(initial?.default_unit ?? "oz");
  const [isUntracked, setIsUntracked] = useState(initial?.is_untracked ?? false);
  const [allergenFlags, setAllergenFlags] = useState<string[]>(initial?.allergen_flags ?? []);
  const [allergenInput, setAllergenInput] = useState("");
  const [notes, setNotes] = useState(initial?.notes ?? "");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // a11y — same dialog treatment the rest of the admin modals get
  // (FlagIngredientModal, IssueModal, ApproveModal, IngredientImpactModal).
  // Focus trap + Escape + focus return + labelled-by title.
  const titleId = useId();
  const nameId = useId();
  const categoryId = useId();
  const unitId = useId();
  const allergenInputId = useId();
  const notesId = useId();
  const { dialogRef } = useDialogA11y(true, busy ? () => {} : onClose);

  function addAllergen() {
    const v = allergenInput.trim();
    if (!v) return;
    if (!allergenFlags.includes(v)) setAllergenFlags([...allergenFlags, v]);
    setAllergenInput("");
  }
  function removeAllergen(a: string) {
    setAllergenFlags(allergenFlags.filter((x) => x !== a));
  }

  async function submit() {
    if (!name.trim()) {
      setErr("Name is required");
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      if (isEdit && initial) {
        await adminUpdateIngredient({
          ingredient_id: initial.id,
          name: name.trim(),
          category,
          default_unit: defaultUnit,
          is_untracked: isUntracked,
          allergen_flags: allergenFlags,
          notes: notes.trim() || null,
        });
        onSaved("Ingredient updated.");
      } else {
        await adminCreateIngredient({
          name: name.trim(),
          category,
          default_unit: defaultUnit,
          is_untracked: isUntracked,
          allergen_flags: allergenFlags,
          notes: notes.trim() || null,
        });
        onSaved("Ingredient created.");
      }
    } catch (e: any) {
      setErr(e?.message ?? "Save failed");
      setBusy(false);
    }
  }

  return (
    <div style={styles.backdrop} onClick={busy ? undefined : onClose}>
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        style={styles.dialog}
        onClick={(e) => e.stopPropagation()}
      >
        <h2 id={titleId} style={styles.modalTitle}>{isEdit ? "Edit Ingredient" : "New Ingredient"}</h2>

        <label htmlFor={nameId} style={styles.label}>Name</label>
        <input
          id={nameId}
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          style={styles.input}
          autoFocus
          disabled={busy}
        />

        <div style={styles.formRow}>
          <div style={{ flex: 1 }}>
            <label htmlFor={categoryId} style={styles.label}>Category</label>
            <select
              id={categoryId}
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              style={styles.input}
              disabled={busy}
            >
              {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div style={{ flex: 1 }}>
            <label htmlFor={unitId} style={styles.label}>Default Unit</label>
            <select
              id={unitId}
              value={defaultUnit}
              onChange={(e) => setDefaultUnit(e.target.value)}
              style={styles.input}
              disabled={busy}
            >
              {UNITS.map((u) => <option key={u} value={u}>{u}</option>)}
            </select>
          </div>
        </div>

        <div style={{ margin: "14px 0" }}>
          <label style={styles.checkboxLabel}>
            <input
              type="checkbox"
              checked={isUntracked}
              onChange={(e) => setIsUntracked(e.target.checked)}
              disabled={busy}
            />
            <span>Basic / Untracked ingredient (exempt from 86 stock-outs and inventory tracking, e.g. water, ice, tap staples)</span>
          </label>
        </div>

        <label htmlFor={allergenInputId} style={styles.label}>Allergens</label>
        <div style={styles.allergenInputRow}>
          <input
            id={allergenInputId}
            type="text"
            value={allergenInput}
            onChange={(e) => setAllergenInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                addAllergen();
              }
            }}
            placeholder="e.g. dairy, nuts, gluten"
            style={{ ...styles.input, flex: 1 }}
            disabled={busy}
          />
          <button type="button" onClick={addAllergen} style={styles.smallBtn} disabled={busy}>
            Add
          </button>
        </div>
        {allergenFlags.length > 0 && (
          <div style={styles.allergenRow}>
            {allergenFlags.map((a) => (
              <span key={a} style={styles.allergenChipEditable}>
                {a}
                <button
                  type="button"
                  onClick={() => removeAllergen(a)}
                  style={styles.chipRemove}
                  disabled={busy}
                >
                  ✕
                </button>
              </span>
            ))}
          </div>
        )}

        <label htmlFor={notesId} style={styles.label}>Notes</label>
        <textarea
          id={notesId}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={2}
          style={styles.textarea}
          placeholder="Optional internal notes (sourcing, brand, prep tips)"
          disabled={busy}
        />

        {err && <div style={styles.error}>{err}</div>}

        <div style={styles.modalActions}>
          <button type="button" onClick={onClose} disabled={busy} style={styles.cancelBtn}>
            Cancel
          </button>
          <button type="button" onClick={submit} disabled={busy} style={styles.confirmBtn}>
            {busy ? "Saving…" : isEdit ? "Save changes" : "Create ingredient"}
          </button>
        </div>
      </div>
    </div>
  );
}

const styles: Record<string, CSSProperties> = {
  shell: { maxWidth: 1200, padding: "8px 0 32px" },
  headerRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-end",
    gap: 12,
    marginBottom: 16,
    flexWrap: "wrap",
  },
  eyebrow: {
    margin: 0,
    fontSize: 11,
    fontWeight: 800,
    letterSpacing: 0.6,
    textTransform: "uppercase",
    color: "var(--gold)",
  },
  title: {
    fontFamily: "Georgia, serif",
    fontSize: 28,
    margin: "8px 0 0",
    letterSpacing: -0.4,
    color: "var(--text)",
  },
  primaryBtn: {
    background: "var(--gold)",
    color: "#0D0B08",
    border: 0,
    borderRadius: 10,
    padding: "8px 14px",
    fontSize: 12,
    fontWeight: 800,
    cursor: "pointer",
  },
  toolbar: {
    display: "flex",
    gap: 12,
    marginBottom: 12,
    flexWrap: "wrap",
    alignItems: "center",
  },
  search: {
    flex: "1 1 240px",
    minWidth: 200,
    background: "var(--bg)",
    color: "var(--text)",
    border: "1px solid var(--border)",
    borderRadius: 10,
    padding: "8px 12px",
    fontSize: 13,
    fontFamily: "inherit",
    outline: "none",
  },
  filterRow: { display: "flex", flexWrap: "wrap", gap: 4 },
  pill: {
    background: "var(--bg)",
    color: "var(--muted)",
    border: "1px solid var(--border)",
    borderRadius: 999,
    padding: "5px 12px",
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: 0.4,
    textTransform: "uppercase",
    cursor: "pointer",
  },
  pillActive: {
    background: "var(--gold)",
    color: "#0D0B08",
    border: "1px solid var(--gold)",
    borderRadius: 999,
    padding: "5px 12px",
    fontSize: 11,
    fontWeight: 800,
    letterSpacing: 0.4,
    textTransform: "uppercase",
    cursor: "pointer",
  },
  muted: { color: "var(--muted)", fontSize: 13 },
  flash: {
    margin: "0 0 12px",
    padding: 10,
    background: "rgba(39,174,96,0.12)",
    border: "1px solid rgba(39,174,96,0.4)",
    color: "#86efac",
    borderRadius: 10,
    fontSize: 12,
    fontWeight: 700,
  },
  error: {
    margin: "0 0 12px",
    padding: 10,
    background: "rgba(224,80,64,0.1)",
    border: "1px solid rgba(224,80,64,0.4)",
    color: "#FF8A7A",
    borderRadius: 10,
    fontSize: 12,
  },
  tableWrap: {
    background: "var(--surface)",
    border: "1px solid var(--border)",
    borderRadius: 12,
    overflowX: "auto",
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
    fontSize: 13,
  },
  th: {
    textAlign: "left",
    padding: "10px 12px",
    color: "var(--muted)",
    fontSize: 11,
    fontWeight: 800,
    letterSpacing: 0.4,
    textTransform: "uppercase",
    borderBottom: "1px solid var(--border)",
  },
  thCenter: {
    textAlign: "center",
    padding: "10px 12px",
    color: "var(--muted)",
    fontSize: 11,
    fontWeight: 800,
    letterSpacing: 0.4,
    textTransform: "uppercase",
    borderBottom: "1px solid var(--border)",
  },
  thRight: {
    textAlign: "right",
    padding: "10px 12px",
    color: "var(--muted)",
    fontSize: 11,
    fontWeight: 800,
    letterSpacing: 0.4,
    textTransform: "uppercase",
    borderBottom: "1px solid var(--border)",
  },
  tr: { borderBottom: "1px solid var(--border)" },
  td: { padding: "10px 12px", color: "var(--text)", verticalAlign: "top" },
  tdCenter: { padding: "10px 12px", color: "var(--text)", textAlign: "center", verticalAlign: "middle" },
  tdRight: { padding: "10px 12px", textAlign: "right", verticalAlign: "middle", whiteSpace: "nowrap" },
  name: { color: "var(--text)" },
  subnote: { color: "var(--muted)", fontSize: 11, marginTop: 2 },
  allergenRow: { display: "flex", flexWrap: "wrap", gap: 4, justifyContent: "center" },
  allergenChip: {
    background: "rgba(249,175,54,0.12)",
    color: "var(--gold)",
    border: "1px solid rgba(249,175,54,0.3)",
    borderRadius: 999,
    padding: "2px 8px",
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: 0.4,
    textTransform: "uppercase",
  },
  allergenChipEditable: {
    background: "rgba(249,175,54,0.12)",
    color: "var(--gold)",
    border: "1px solid rgba(249,175,54,0.3)",
    borderRadius: 999,
    padding: "2px 4px 2px 10px",
    fontSize: 11,
    fontWeight: 700,
    display: "inline-flex",
    alignItems: "center",
    gap: 4,
  },
  chipRemove: {
    background: "transparent",
    border: 0,
    color: "var(--gold)",
    cursor: "pointer",
    fontSize: 12,
    padding: "0 4px",
  },
  statusAvail: {
    background: "rgba(39,174,96,0.16)",
    color: "#86efac",
    border: "1px solid rgba(39,174,96,0.3)",
    borderRadius: 999,
    padding: "2px 8px",
    fontSize: 10,
    fontWeight: 800,
    letterSpacing: 0.4,
    textTransform: "uppercase",
  },
  statusUnavail: {
    background: "rgba(224,80,64,0.16)",
    color: "#FF8A7A",
    border: "1px solid rgba(224,80,64,0.4)",
    borderRadius: 999,
    padding: "2px 8px",
    fontSize: 10,
    fontWeight: 800,
    letterSpacing: 0.4,
    textTransform: "uppercase",
  },
  statusUntracked: {
    background: "rgba(56, 189, 248, 0.14)",
    color: "#7dd3fc",
    border: "1px solid rgba(56, 189, 248, 0.35)",
    borderRadius: 999,
    padding: "2px 8px",
    fontSize: 10,
    fontWeight: 800,
    letterSpacing: 0.4,
    textTransform: "uppercase",
  },
  untrackedBadge: {
    display: "inline-block",
    marginLeft: 6,
    background: "rgba(56, 189, 248, 0.12)",
    color: "#7dd3fc",
    border: "1px solid rgba(56, 189, 248, 0.3)",
    borderRadius: 4,
    padding: "1px 5px",
    fontSize: 9,
    fontWeight: 800,
    letterSpacing: 0.3,
    textTransform: "uppercase",
    verticalAlign: "middle",
  },
  alwaysAvailLabel: {
    display: "inline-block",
    padding: "8px 12px",
    minHeight: 40,
    fontSize: 11,
    fontWeight: 700,
    color: "var(--muted)",
    marginRight: 6,
    whiteSpace: "nowrap",
    lineHeight: "24px",
  },
  checkboxLabel: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    fontSize: 12,
    color: "var(--text)",
    cursor: "pointer",
  },
  btnGreen: {
    background: "rgba(39,174,96,0.18)",
    color: "#86efac",
    border: "1px solid rgba(39,174,96,0.4)",
    borderRadius: 8,
    padding: "8px 12px",
    minHeight: 40,
    fontSize: 11,
    fontWeight: 700,
    cursor: "pointer",
    marginRight: 6,
    whiteSpace: "nowrap",
  },
  btnRed: {
    background: "rgba(224,80,64,0.16)",
    color: "#FF8A7A",
    border: "1px solid rgba(224,80,64,0.4)",
    borderRadius: 8,
    padding: "8px 12px",
    minHeight: 40,
    fontSize: 11,
    fontWeight: 700,
    cursor: "pointer",
    marginRight: 6,
    whiteSpace: "nowrap",
  },
  btnSecondary: {
    background: "transparent",
    color: "var(--text)",
    border: "1px solid var(--border)",
    borderRadius: 8,
    padding: "8px 12px",
    minHeight: 40,
    fontSize: 11,
    fontWeight: 700,
    cursor: "pointer",
    whiteSpace: "nowrap",
  },
  empty: {
    textAlign: "center",
    padding: 24,
    color: "var(--muted)",
    fontStyle: "italic",
    fontSize: 13,
  },

  // Modal styles
  backdrop: {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.65)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 16,
    zIndex: 99,
  },
  dialog: {
    width: "100%",
    maxWidth: 520,
    maxHeight: "90vh",
    overflowY: "auto",
    background: "var(--surface)",
    border: "1px solid var(--border)",
    borderRadius: 16,
    padding: 20,
    boxShadow: "0 8px 28px rgba(0,0,0,0.5)",
  },
  modalTitle: {
    fontFamily: "Georgia, serif",
    fontSize: 22,
    margin: "0 0 12px",
    color: "var(--text)",
  },
  label: {
    display: "block",
    fontSize: 11,
    fontWeight: 800,
    letterSpacing: 0.4,
    textTransform: "uppercase",
    color: "var(--muted)",
    marginTop: 10,
    marginBottom: 6,
  },
  input: {
    width: "100%",
    boxSizing: "border-box",
    background: "var(--bg)",
    color: "var(--text)",
    border: "1px solid var(--border)",
    borderRadius: 10,
    padding: 8,
    fontSize: 13,
    fontFamily: "inherit",
    outline: "none",
  },
  formRow: { display: "flex", gap: 12 },
  textarea: {
    width: "100%",
    boxSizing: "border-box",
    background: "var(--bg)",
    color: "var(--text)",
    border: "1px solid var(--border)",
    borderRadius: 10,
    padding: 8,
    fontSize: 13,
    fontFamily: "inherit",
    resize: "vertical",
    outline: "none",
  },
  allergenInputRow: { display: "flex", gap: 6 },
  smallBtn: {
    background: "var(--bg)",
    color: "var(--text)",
    border: "1px solid var(--border)",
    borderRadius: 8,
    padding: "6px 12px",
    fontSize: 12,
    fontWeight: 700,
    cursor: "pointer",
  },
  modalActions: {
    display: "flex",
    gap: 8,
    justifyContent: "flex-end",
    marginTop: 16,
  },
  secondaryBtn: {
    background: "rgba(249, 175, 54, 0.12)",
    color: "var(--gold)",
    border: "1px solid rgba(249, 175, 54, 0.3)",
    borderRadius: 10,
    padding: "8px 14px",
    fontSize: 12,
    fontWeight: 800,
    cursor: "pointer",
  },
  modalHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: 16,
    borderBottom: "1px solid var(--border)",
    paddingBottom: 12,
  },
  modalMeta: {
    fontSize: 12,
    color: "var(--muted)",
    margin: "4px 0 0",
  },
  closeBtn: {
    background: "none",
    border: "none",
    fontSize: 20,
    color: "var(--muted)",
    cursor: "pointer",
    padding: "2px 8px",
  },
  essentialChip: {
    background: "rgba(249, 175, 54, 0.12)",
    color: "var(--gold)",
    border: "1px solid rgba(249, 175, 54, 0.3)",
    borderRadius: 8,
    padding: "5px 10px",
    fontSize: 12,
    fontWeight: 700,
    cursor: "pointer",
  },
  essentialChipExists: {
    background: "rgba(255, 255, 255, 0.05)",
    color: "var(--muted)",
    border: "1px solid var(--border)",
    cursor: "default",
  },
  customAddBox: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    background: "rgba(249, 175, 54, 0.06)",
    border: "1px solid rgba(249, 175, 54, 0.2)",
    borderRadius: 10,
    padding: "10px 14px",
    marginBottom: 12,
    gap: 12,
    flexWrap: "wrap",
  },
  pullBtn: {
    background: "var(--gold)",
    color: "#0D0B08",
    border: 0,
    borderRadius: 8,
    padding: "5px 10px",
    fontSize: 12,
    fontWeight: 800,
    cursor: "pointer",
  },
  cancelBtn: {
    background: "transparent",
    color: "var(--muted)",
    border: "1px solid var(--border)",
    borderRadius: 10,
    padding: "8px 14px",
    fontSize: 12,
    fontWeight: 800,
    cursor: "pointer",
  },
  confirmBtn: {
    background: "var(--gold)",
    color: "#0D0B08",
    border: 0,
    borderRadius: 10,
    padding: "8px 14px",
    fontSize: 12,
    fontWeight: 800,
    cursor: "pointer",
  },
};
